// Translation helper
import enTranslations from "./en.json";
import frTranslations from "./fr.json";

const translations = {
  en: enTranslations,
  fr: frTranslations
};

/**
 * Translation function with support for chef/restaurant variants
 * @param {string} key - The translation key (e.g., "common.best_chefs_earn")
 * @param {string} lang - Language code (default: "en")
 * @param {boolean} isRestaurant - If true, uses restaurant variant of the key (default: false)
 * @returns {string} - The translated string
 */
export const t = (key, lang = "en", isRestaurant = false) => {
  // Validate inputs
  if (!key || typeof key !== "string") {
    return key || "";
  }

  if (!translations[lang]) {
    lang = "en"; // Fallback to English
  }

  let keys = key.split(".");
  let value = undefined; // Start with undefined, not the entire translations object

  // If isRestaurant is true, try restaurant variant first
  if (isRestaurant) {
    let restaurantKeys = [...keys];
    const lastKey = restaurantKeys[restaurantKeys.length - 1];

    // Replace chef with restaurant or _ch with _restaurant
    if (
      lastKey.endsWith("_ch") ||
      lastKey.endsWith("_ch_1") ||
      lastKey.endsWith("_ch_2") ||
      lastKey.endsWith("_ch_3")
    ) {
      restaurantKeys[restaurantKeys.length - 1] = lastKey.replace(
        /_ch(_\d+)?$/,
        "_restaurant$1"
      );
    } else if (lastKey.toLowerCase().includes("chef")) {
      restaurantKeys[restaurantKeys.length - 1] = lastKey.replace(
        /chef/gi,
        "restaurant"
      );
    }

    // Try restaurant variant first
    let restaurantValue = translations[lang];
    for (const k of restaurantKeys) {
      if (restaurantValue && typeof restaurantValue === "object") {
        restaurantValue = restaurantValue[k];
      } else {
        restaurantValue = undefined;
        break;
      }
    }

    // If restaurant variant found, use it
    if (restaurantValue !== undefined) {
      value = restaurantValue;
    } else {
      // Fallback to original chef key
      value = undefined;
    }
  }

  // If value is still undefined, try the original key
  if (value === undefined) {
    keys = key.split(".");
    value = translations[lang];
    for (const k of keys) {
      if (value && typeof value === "object") {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }
  }

  // If not found in requested language, try English
  if (value === undefined) {
    // Try restaurant variant in English if isRestaurant is true
    if (isRestaurant && lang !== "en") {
      let restaurantKeys = key.split(".");
      const lastKey = restaurantKeys[restaurantKeys.length - 1];

      if (
        lastKey.endsWith("_ch") ||
        lastKey.endsWith("_ch_1") ||
        lastKey.endsWith("_ch_2") ||
        lastKey.endsWith("_ch_3")
      ) {
        restaurantKeys[restaurantKeys.length - 1] = lastKey.replace(
          /_ch(_\d+)?$/,
          "_restaurant$1"
        );
      } else if (lastKey.toLowerCase().includes("chef")) {
        restaurantKeys[restaurantKeys.length - 1] = lastKey.replace(
          /chef/gi,
          "restaurant"
        );
      }

      let fallbackValue = translations.en;
      for (const k of restaurantKeys) {
        if (fallbackValue && typeof fallbackValue === "object") {
          fallbackValue = fallbackValue[k];
        } else {
          fallbackValue = undefined;
          break;
        }
      }
      if (fallbackValue !== undefined) {
        value = fallbackValue;
      }
    }

    // If still undefined, try original key in English
    if (value === undefined) {
      let fallbackValue = translations.en;
      for (const k of keys) {
        if (fallbackValue && typeof fallbackValue === "object") {
          fallbackValue = fallbackValue[k];
        } else {
          fallbackValue = undefined;
          break;
        }
      }
      value = fallbackValue;
    }
  }

  // Ensure we return a string, not an object
  // If value is an object (and not null), it means we didn't find the final translation
  // React cannot render objects, so we must return a string
  if (value !== null && value !== undefined && typeof value === "object") {
    // If it's an array, join it (though arrays should be handled differently)
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    // Otherwise, return the key as fallback
    return key;
  }

  // Return the string value or the key as fallback
  // Ensure we always return a string
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return key;
};

export default translations;
